// Sugalarm — suppress the native long-press / right-click selection +
// context menu app-wide (cycle 210).
//
// The CSS in `tokens.css` (`user-select: none` + `-webkit-touch-callout:
// none` on the app root) removes the selection highlight and the iOS/
// WebView callout; this is the belt-and-suspenders JS half that
// `preventDefault()`s the `contextmenu` event so the long-press / right-
// click menu (Copy · Select all · Share · Web search · Manage apps) never
// opens. On a locked kiosk that menu's Web-search / Manage-apps rows are
// an escape hatch, so this is hardening as much as polish.
//
// Scope:
//   - kiosk → suppress unconditionally (text entry is the cycle-14 on-
//     screen keyboard, never a native paste menu).
//   - web / mobile → suppress everywhere EXCEPT editable fields, so the
//     native paste menu still works in inputs / textareas.
//
// A `selectstart` suppressor for non-editable targets is added as a
// belt against desktop drag-select. Both listeners are capture-phase
// and only `preventDefault()` — never `stopPropagation()` — so existing
// pointer / gesture handlers (HistoryChart pan/pinch, the landscape
// pager swipes, the cycle-115 haptic taps, the cycle-208 reveal-tap) are
// completely unaffected.

import { useEffect } from "react";

/** True when the event target is (or sits inside) an editable control. */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return (
    target.closest(
      'input, textarea, [contenteditable]:not([contenteditable="false"])',
    ) != null
  );
}

/**
 * Mount the global selection / context-menu suppressor. Pass whether the
 * current surface is a kiosk — on a kiosk the suppression is
 * unconditional (including inside inputs); elsewhere editable fields keep
 * their native paste menu.
 */
export function useSuppressNativeMenu(kiosk: boolean): void {
  useEffect(() => {
    const onContextMenu = (e: Event) => {
      // web / mobile: let editable fields keep their native paste menu.
      if (!kiosk && isEditableTarget(e.target)) return;
      e.preventDefault();
    };
    const onSelectStart = (e: Event) => {
      // Never block selection inside editable fields (caret + drag-select
      // within an input must still work on every surface).
      if (isEditableTarget(e.target)) return;
      e.preventDefault();
    };
    document.addEventListener("contextmenu", onContextMenu, true);
    document.addEventListener("selectstart", onSelectStart, true);
    return () => {
      document.removeEventListener("contextmenu", onContextMenu, true);
      document.removeEventListener("selectstart", onSelectStart, true);
    };
  }, [kiosk]);
}
